import { BodyText, Callout } from "@/components/ui";

import { useParams } from "@tanstack/react-router";

import {
  type AllergyStatus,
  usePatientAllergies,
} from "@/features/Patient/MedicalRecords/MedicationsAllergies/network/useAllergies";
import { Fragment } from "react/jsx-runtime";
import { EntrySkeleton } from "../EntrySkeleton";
import { AllergyEntry } from "./AllergyEntry";

interface AllergiesSectionProps {
  status: AllergyStatus;
  isBatchConfirmingAllergies: boolean;
}

export const AllergiesSection = ({
  status,
  isBatchConfirmingAllergies,
}: AllergiesSectionProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });

  const {
    data: patientAllergyData,
    isLoading: isAllergiesLoading,
    isError: isAllergiesError,
  } = usePatientAllergies({ patientId, status });

  const isAllergiesEmpty =
    !isAllergiesLoading &&
    !isAllergiesError &&
    (!patientAllergyData || patientAllergyData?.allergies?.length === 0);

  if (isAllergiesEmpty) {
    return (
      <BodyText textColor="tertiary" size="sm">
        {`No ${status} allergies documented`}
      </BodyText>
    );
  }

  return (
    <>
      {(isAllergiesLoading || !patientId) && <EntrySkeleton />}
      {isAllergiesError && (
        <div className="flex relative flex-col gap-2 px-3 py-2 w-full">
          <Callout
            className="w-full"
            variant="danger"
            title="Something went wrong!"
            description="Please refresh and try again."
          />
        </div>
      )}
      {!!patientId &&
        patientAllergyData?.allergies?.map((allergy) => (
          <Fragment key={allergy.allergyIntolerance.id}>
            <AllergyEntry
              allergy={allergy}
              status={status}
              isBatchConfirmingAllergies={isBatchConfirmingAllergies}
            />
          </Fragment>
        ))}
    </>
  );
};
